import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  SendOTPThroughExistingUser,
  addUserThroughExistingCompany,
  getUserFirmsAction,
} from './APIs/action';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { addEmployeePermissions, getRoleLabel } from '../../utils/settings';
import OTPInput from '../Onboarding/Component/OtpInput';
import SendOtp from '../Onboarding/Component/SendOtp';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { alphabeticRegex } from '../Utils';

const AddNewEmployee = () => {
  const navigate = useNavigate();

  const [firmList, setFirmList] = useState([]);
  const [errors, setErrors] = useState({});

  const [employee, setEmployee] = useState({
    firstname: '',
    lastname: '',
    employee_roles: [],
    employee_phone: '',
    firm_id: null,
    otp: '',
  });

  const [OTPSent, setOTPSent] = useState(false);
  const allFirms = async () => {
    const { data, error, message } = await getUserFirmsAction();

    if (!error) {
      setFirmList(
        data.map((vv) => ({
          label: vv.company_name,
          value: vv.company_id,
        })),
      );
    } else {
      toast.error(message);
    }
  };

  useEffect(() => {
    allFirms();
  }, []);

  const handleOtpSend = async () => {
    const newErrors = {};
    if (!employee.employee_phone)
      newErrors.employee_phone = 'Enter a valid mobile number';

    if (employee.employee_phone.length < 10)
      newErrors.employee_phone = 'Enter a 10-digit mobile number';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const { error, message, data } = await SendOTPThroughExistingUser({
      phone_number: employee.employee_phone,
    });
    if (!error) {
      toast.success('OTP sent successfully');
      setOTPSent(true);
    } else {
      toast.error(message);
    }
  };
  const onOnboardEmployee = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!employee.employee_phone)
      newErrors.employee_phone = 'Enter a valid mobile number';

    if (employee.employee_phone.length < 10)
      newErrors.employee_phone = 'Enter a 10-digit mobile number';

    if (!employee.firstname) newErrors.firstname = 'Please enter employee name';

    if (!employee.firm_id) newErrors.firm_id = 'Please select employee firm';

    if (employee.employee_roles.length === 0)
      newErrors.employee_roles = 'Please select employee roles';

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }
    if (!employee.otp || employee.otp.length < 4) {
      return toast.error('Please enter a valid OTP');
    }

    const payload = {
      phone_number: employee.employee_phone,
      otp: employee.otp,
      firstname: employee.firstname,
      lastname: employee.lastname,
      privacy_policy_accept_status: true,
      company_id: employee.firm_id,
      permissions: employee.employee_roles,
    };

    const { data, error, message } = await addUserThroughExistingCompany(
      payload,
    );

    if (!error) {
      navigate('/my-account/employee-details');
      toast.success('Employee added successfully');
    } else {
      toast.error(message);
    }
  };

  return (
    <>
      <Container
        maxWidth='true'
        sx={{ pl: '2rem', pt: '5rem' }}
      >
        <Stack
          spacing={2}
          direction='row'
        >
          <IconButton
            aria-label='Screenback'
            sx={{ color: 'black' }}
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant='h4'>Add New Employee</Typography>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: '3rem' }}>
          <Card
            sx={{
              p: '4rem',
              width: '50%',
              height: 'auto',
            }}
          >
            <form onSubmit={onOnboardEmployee}>
              <FormControl
                sx={{ width: '100%' }}
                error={!!errors.firm_id}
              >
                <InputLabel id='select-firm'>Select Firm</InputLabel>
                <Select
                  label='Select Firm'
                  onChange={(e) => {
                    setEmployee({
                      ...employee,
                      firm_id: e.target.value,
                    });
                  }}
                  value={employee?.firm_id}
                >
                  {firmList &&
                    firmList.length > 0 &&
                    firmList.map((firm) => (
                      <MenuItem value={firm.value}>{firm.label}</MenuItem>
                    ))}
                </Select>
                {errors.firm_id && (
                  <FormHelperText>{errors.firm_id}</FormHelperText>
                )}
              </FormControl>
              <FormControl
                sx={{ width: '100%' }}
                margin='normal'
                error={!!errors.firstname}
              >
                <TextField
                  fullWidth
                  id='firstname'
                  type='text'
                  value={employee.firstname}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (alphabeticRegex.test(value)) {
                      setEmployee({
                        ...employee,
                        firstname: value,
                      });
                    }
                  }}
                  label='Employee First Name'
                  error={!!errors.firstname}
                  helperText={errors.firstname || ''}
                />
              </FormControl>
              <FormControl
                sx={{ width: '100%' }}
                margin='normal'
              >
                <TextField
                  fullWidth
                  id='lastname'
                  type='text'
                  value={employee.lastname}
                  onChange={(e) => {
                    setEmployee({
                      ...employee,
                      lastname: e.target.value,
                    });
                  }}
                  label='Employee Last Name'
                />
              </FormControl>

              <FormControl
                sx={{ width: '100%', mt: 1 }}
                margin='normal'
                error={!!errors.employee_phone}
              >
                <TextField
                  fullWidth
                  id='employee_phone'
                  type='text'
                  value={employee.employee_phone}
                  onChange={(e) => {
                    if (
                      /^\d*$/.test(e.target.value) &&
                      e.target.value.length <= 10
                    ) {
                      setEmployee({
                        ...employee,
                        employee_phone: e.target.value,
                      });
                    }
                  }}
                  error={!!errors.employee_phone}
                  helperText={errors.employee_phone || ''}
                  label='Employee Phone Number'
                />
              </FormControl>

              <FormControl
                sx={{ width: '100%', mt: 1, mb: 2 }}
                error={!!errors.employee_roles}
              >
                <InputLabel id='multiple-chip-label'>Select Roles</InputLabel>
                <Select
                  labelId='multiple-chip-label'
                  id='multiple-chip'
                  multiple
                  value={employee.employee_roles}
                  onChange={(e) => {
                    setEmployee({
                      ...employee,
                      employee_roles: e.target.value,
                    });
                  }}
                  input={
                    <OutlinedInput
                      id='select-multiple-chip'
                      label='Select Roles'
                    />
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={getRoleLabel(value)}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {addEmployeePermissions.map((role) => (
                    <MenuItem
                      key={role.value}
                      value={role.value}
                    >
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.employee_roles && (
                  <FormHelperText>{errors.employee_roles}</FormHelperText>
                )}
              </FormControl>
              <Grid
                container
                sx={{ mb: 4 }}
              >
                <Grid
                  item
                  xs={6}
                  md={3}
                  alignContent='center'
                >
                  <Typography
                    variant='h6'
                    fontSize={15}
                  >
                    Enter OTP
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  md={9}
                  alignContent='center'
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <OTPInput
                    onOtpChange={(otp) => {
                      setEmployee({ ...employee, otp: otp });
                    }}
                    isDark={false}
                  />
                  <SendOtp
                    onResend={handleOtpSend}
                    OTPSent={OTPSent}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, width: '100%' }}>
                <Button
                  type='submit'
                  variant='outlined'
                  color='primary'
                  fullWidth
                >
                  Add Employee
                </Button>
              </Box>
            </form>
          </Card>
        </Box>
      </Container>
      <ToastContainer />
    </>
  );
};
export default AddNewEmployee;
